import {
  PIPELINE_ACTION_ACTIVATE,
  PIPELINE_ACTION_DELETE,
  PIPELINE_ACTION_PAUSE,
  ARCHIVAL_STATUS_ARCHIVED,
  DELETE_DISABLED_MESSAGES,
  FLOW_TYPE_API_SERVER,
} from 'js/constants/pipelineConstants';
import { canActivateResource, createDeleteActionValidator } from 'js/lib/flowValidationUtils';
import { ReactComponent as PauseIcon } from 'new/components/icons/pause.svg';
import { restrictMenuForRole } from 'js/lib/ACLUtils';
import { ReactComponent as ActivateIcon } from 'new/components/icons/bolt.svg';
import { ReactComponent as DuplicateIcon } from 'new/components/icons/copyAlt.svg';
import { ReactComponent as DetailsIcon } from 'new/components/icons/fi-rr-info.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as RestoreIcon } from 'new/components/icons/fi-rr-undo.svg';
import { ReactComponent as KeyIcon } from 'new/components/icons/key.svg';
import { ReactComponent as FolderIcon } from 'new/components/icons/folder.svg';
import { ReactComponent as InsightsOutlined } from 'new/components/icons/InsightsOutlined.svg';
import { FlowDetailActionsMode } from 'new/constants/flowDetailsActionsMode';
import { DetailsIconButton } from 'new/elements/Button';
import { ModalMoveToProject } from 'new/elements/ModalMoveToProject';
import { ModalsContext } from 'new/routes/ModalsContext';
import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from 'new/hooks/useAppContext';
import { isFullScreenCanvasURL } from 'js/lib/URLUtils';
import Menu from '../../components/elements/menu/Menu';
import s from './DataFlowDetailActions.module.scss';
import { isFlowNodeResource } from 'js/lib/ResourceUtils';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { IconButton, Tooltip } from '@mui/material';
import {
  useTriageLauncher,
  buildTriageContextFromResource,
} from 'new/components/monitor/TriageChat';

function DataFlowDetailActions(props) {
  const {
    projectId,
    inPipeline,
    data,
    insightsOnly, // Keep for backward compatibility
    hideMenuDetails,
    isSecondaryFlow,
    isExpandedCanvas,
    embedded,
    flowNode,
  } = props;

  const { openModal } = useContext(ModalsContext);
  const { mode } = useAppContext();
  const triage = useTriageLauncher();
  const { pathname } = useLocation();
  const isFullScreenCanvas = isFullScreenCanvasURL(pathname);
  const allowDeleteFlow =
    (!isFullScreenCanvas || !!isExpandedCanvas) && !isSecondaryFlow && !embedded;
  // API server flows own API keys, URLs and endpoint resources that admin-api can't
  // copy yet, so a duplicate comes out broken — hide the option rather than mislead.
  const isApiServerFlow = (data?.flow_type ?? flowNode?.flow_type) === FLOW_TYPE_API_SERVER;
  const allowDuplicateFlow =
    (!isFullScreenCanvas || !!isExpandedCanvas) &&
    !isSecondaryFlow &&
    !embedded &&
    !isApiServerFlow &&
    typeof props.toggleCloneFlowModal === 'function';
  const allowActivateFlow = !isSecondaryFlow;
  let menuItems = [];
  const isFlowNode = isFlowNodeResource(data);
  const flowNodeID = props?.data?.['flow_node_id'] ?? (isFlowNode ? props?.data?.['id'] : null);

  const activationValidation = canActivateResource(flowNode); // checks if flow can be activated (checks archival status)

  // Determine effective mode at top level
  const effectiveMode = insightsOnly
    ? FlowDetailActionsMode.INSIGHTS_ONLY
    : FlowDetailActionsMode.DEFAULT;

  if (effectiveMode === FlowDetailActionsMode.INSIGHTS_ONLY) {
    menuItems.push({
      label: 'Flow Insights',
      icon: <InsightsOutlined />,
      onClick: _handleViewInsights,
    });
  } else {
    const projectActionLabel = projectId ? 'Remove from Project' : 'Move to Project';
    menuItems.push({
      label: 'Flow',
      type: 'heading',
    });

    if (!hideMenuDetails) {
      menuItems.push({
        label: 'Details',
        icon: <DetailsIcon />,
        onClick: _openOverViewTab,
      });
    }

    const isArchived = flowNode?.archival_status === ARCHIVAL_STATUS_ARCHIVED;
    const shouldShowRestore = !activationValidation.canActivate && isArchived;

    /**
     * Add Restore Flow option for archived flows (shown first)
     * - only show if flow is archived
     */
    if (shouldShowRestore) {
      menuItems.push({
        label: 'Restore Flow',
        icon: <RestoreIcon />,
        onClick: _handleRestoreFlowClicked,
      });
    }

    if (allowActivateFlow) {
      menuItems.push(
        {
          label: 'Activate Flow',
          icon: <ActivateIcon />,
          disable: !activationValidation.canActivate,
          disableMessage: activationValidation.reason,
          onClick: _handleActivatePipelineClicked,
        },
        {
          label: 'Pause Flow',
          icon: <PauseIcon />,
          disable: isArchived,
          disableMessage: isArchived
            ? 'Cannot pause archived flows. Please restore the flow first.'
            : undefined,
          onClick: _handlePausePipelineClicked,
        },
      );
    }

    // Hide Delete Flow option based on context
    if (allowDeleteFlow) {
      menuItems.push({
        label: 'Delete Flow',
        icon: <DeleteIcon />,
        color: 'error',
        onClick: _handleDeleteFlowClicked,
      });
    }

    if (allowDuplicateFlow) {
      // NEX-19683: Only render the Duplicate Flow menu item if the modal action is present
      menuItems.push({
        label: 'Duplicate Flow',
        icon: <DuplicateIcon />,
        onClick: _openDuplicateFlow,
      });
    }

    menuItems.push({
      label: 'Flow Insights',
      icon: <InsightsOutlined />,
      onClick: _handleViewInsights,
    });

    if (!embedded) {
      menuItems.push(
        {
          label: 'Access Control',
          icon: <KeyIcon />,
          onClick: _handleAccessControl,
        },
        {
          label: 'Project',
          type: 'heading',
        },
        {
          label: projectActionLabel,
          icon: (
            <FolderIcon
              style={{
                fontSize: 19,
              }}
            />
          ),
          onClick: projectId ? _handleRemoveFlowFromProject : _handleMoveToProject,
        },
      );
    }
  }

  const { hideZoomIcon } = props;

  menuItems = restrictMenuForRole(menuItems, data);
  const details = (
    <DetailsIconButton
      onClick={_openOverViewTab}
      className={mode === 'dark' ? s.darkIcon : undefined}
    />
  );

  const triageButton = triage.enabled ? (
    <Tooltip title="Triage with AI" placement="bottom">
      <IconButton
        aria-label="Triage with AI"
        data-testid="triage-flow-list-button"
        // Match the adjacent Details (info) icon's 16x16 flush box so the two
        // sit on the same baseline and read at the same visual weight.
        sx={{ width: 16, height: 16, padding: 0, color: 'inherit' }}
        onClick={(e) => {
          e.stopPropagation();
          triage.open(
            buildTriageContextFromResource({
              resourceType: 'flows',
              resourceId: flowNodeID,
              resourceName: data?.name,
            }),
            'flows_listing',
          );
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <div className={s.actionsContainer}>
      {triageButton}
      {effectiveMode !== FlowDetailActionsMode.INSIGHTS_ONLY &&
        !hideZoomIcon &&
        (props.DetailComponent
          ? React.cloneElement(props.DetailComponent, {
              onClick: _openOverViewTab,
            })
          : details)}

      <div>
        <Menu horizontalDots items={menuItems} inPipeline={inPipeline} />
      </div>
    </div>
  );

  function _openDuplicateFlow() {
    props.toggleCloneFlowModal(props.data);
  }

  function _handlePausePipelineClicked() {
    props.setPipelineAction(flowNodeID, PIPELINE_ACTION_PAUSE);
  }

  function _handleActivatePipelineClicked() {
    props.setPipelineAction(flowNodeID, PIPELINE_ACTION_ACTIVATE);
  }

  function _handleDeleteFlowClicked() {
    props.setPipelineAction(flowNodeID, PIPELINE_ACTION_DELETE);
  }

  function _handleRestoreFlowClicked() {
    props.restorePipeline(flowNodeID).catch(console.error);
  }

  function _openOverViewTab(e) {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    let { data } = props;
    const isFlowNode = isFlowNodeResource(data);
    // Ensure nodeKey is set - FlowDetailViewContainer uses it to look up flowNodes[nodeKey]
    // nodeKey should be origin_node_id or flow_node_id (the flow ID, not the source ID)
    const nodeKey =
      data?.nodeKey ||
      data?.origin_node_id ||
      data?.flow_node_id ||
      (isFlowNode ? data?.id : undefined);
    if (!nodeKey) {
      console.warn(
        '[DataFlowDetailActions] Cannot open overview: missing nodeKey/origin_node_id',
        data,
      );
      return;
    }
    const flowData = {
      ...data,
      nodeKey: nodeKey,
      id: data?.id || nodeKey,
      type: data?.type || 'SOURCE',
    };
    props.setDetailViewCard(flowData);
  }

  function _handleViewInsights() {
    let { data, setDetailViewCard } = props;
    const isFlowNode = isFlowNodeResource(data);
    // Ensure nodeKey is set
    const nodeKey =
      data?.nodeKey ||
      data?.origin_node_id ||
      data?.flow_node_id ||
      (isFlowNode ? data?.id : undefined);
    if (!nodeKey) {
      console.warn('[DataFlowDetailActions] Cannot open insights: missing nodeKey', data);
      return;
    }
    const flowData = {
      ...data,
      nodeKey: nodeKey,
      id: data?.id || nodeKey,
      type: data?.type || 'SOURCE',
    };
    props.setDetailViewCard(flowData, 'Insights');
    setTimeout(() => {
      setDetailViewCard(flowData);
    }, 200);
  }

  function _handleAccessControl() {
    let { data, setDetailViewCard } = props;
    const isFlowNode = isFlowNodeResource(data);
    // Ensure nodeKey is set
    const nodeKey =
      data?.nodeKey ||
      data?.origin_node_id ||
      data?.flow_node_id ||
      (isFlowNode ? data?.id : undefined);
    if (!nodeKey) {
      console.warn('[DataFlowDetailActions] Cannot open access control: missing nodeKey', data);
      return;
    }
    const flowData = {
      ...data,
      nodeKey: nodeKey,
      id: data?.id || nodeKey,
      type: data?.type || 'SOURCE',
    };
    props.setDetailViewCard(flowData, 'Access');
    setTimeout(() => {
      setDetailViewCard(flowData);
    }, 200);
  }

  function _handleMoveToProject() {
    openModal(ModalMoveToProject.modalConfig.name, {
      ...props,
      item: props.data,
    });
  }

  function _handleRemoveFlowFromProject() {
    const flowNodeId = props.data?.origin_node_id;

    props
      .removeFlowFromProject({
        id: flowNodeId,
        projectId: props.projectId,
      })
      .then(() => {
        if (flowNodeId) {
          props.refreshPipeline(flowNodeId);
        }
      });
  }
}

DataFlowDetailActions.defaultProps = {
  hideMenuDetails: false,
};

export default DataFlowDetailActions;
